import { UIData } from '../../hooks/stores/uiStore';
import { LauncherElementData } from '../../hooks/stores/launcherElementsStore';
import { LauncherStatesData } from '../../hooks/stores/launcherStatesStore';
import { LauncherElementPropInput, launcherElementInfo } from '../../info/launcherElementInfo';
import { convertStringFromCamelCaseToKebabCase } from '../../functions/utils/convertStringFromCamelCaseToKebabCase';
import { convertObjectKeysFromKebabCaseToCamelCase } from '../../functions/utils/convertObjectKeysFromKebabCaseToCamelCase';
import { updateObjectNestedProperties } from '../../functions/launcherElement/updateObjectNestedProperties';
import { getObjectNestedProperties } from '../../functions/launcherElement/getObjectNestedProperties';

interface ElementDataUtilsDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  launcherElementsData: Record<string, LauncherElementData>;
  setLauncherElementsData: (data: Partial<Record<string, LauncherElementData>>, replace?: boolean) => void;
}

export function createElementDataUtils({
  UIData,
  setUIData,
  launcherElementsData,
  setLauncherElementsData,
}: ElementDataUtilsDependencies) {
  const _getSelectorString = (
    launcherState?: [keyof LauncherStatesData | null, string | null],
    pseudoclass?: string
  ) => {
    const [state, substate] = launcherState || UIData.currentLauncherState;

    const variantString =
      state === null
        ? ''
        : '.variants.' + convertStringFromCamelCaseToKebabCase(state);

    const substateString = substate === null ? '' : `.${substate}`;

    const pseudoclassString =
      pseudoclass || UIData.pseudoclass ? `.&:${UIData.pseudoclass}` : '';

    return `props.class${variantString}${substateString}${pseudoclassString}`;
  };

  const updateElementData = (
    id: string,
    data: { [key: string]: any },
    customSelectorString?: string
  ) => {
    const element = launcherElementsData[id];
    if (!element) return;

    const selectorString =
      customSelectorString === undefined
        ? _getSelectorString()
        : customSelectorString;

    const updatedElement = updateObjectNestedProperties(
      element,
      data,
      selectorString
    );

    setLauncherElementsData({
      [id]: updatedElement,
    });
  };

  const updateSelectedElementsData = (
    data: { [key: string]: any },
    customSelectorString?: string,
    inputType?: LauncherElementPropInput
  ) => {
    UIData.selectedElementIDs.forEach((id) => {
      if (inputType) {
        const element = launcherElementsData[id];
        if (!element) return;

        const dataCanBeApplied =
          launcherElementInfo[element.type].propInputs?.includes(inputType);
        if (!dataCanBeApplied) return;
      }
      updateElementData(id, data, customSelectorString);
    });
  };

  const getElementData = (
    id: string,
    defaultObj: Record<string, any>,
    customSelectorString?: string
  ) => {
    const element = launcherElementsData[id];
    if (!element) return defaultObj;

    const selectorStringForCurrentState = _getSelectorString();

    return getObjectNestedProperties(
      element,
      defaultObj,
      customSelectorString === undefined
        ? selectorStringForCurrentState
        : customSelectorString
    );
  };

  const getSelectedElementsData = (
    defaultObj: Record<string, any>,
    customSelectorString?: string
  ) => {
    let result: Record<string, any> = {};

    UIData.selectedElementIDs.map((id, index) => {
      const data = getElementData(id, defaultObj, customSelectorString);

      if (index === 0) {
        result = { ...data };
        return;
      }

      for (let key in result) {
        if (result[key] === data[key]) {
          result[key] = data[key];
        } else {
          result[key] = null; // mixed value
        }
      }
    });

    return result;
  };

  const getSelectedElementsPropInputs = () => {
    const uniqueInputsSet = new Set<LauncherElementPropInput>();

    UIData.selectedElementIDs.forEach((id) => {
      const element = launcherElementsData[id];
      const inputs = launcherElementInfo[element?.type]?.propInputs || [];
      inputs.forEach((input) => uniqueInputsSet.add(input));
    });

    return [...uniqueInputsSet];
  };

  const getElementComputedStyle = (id: string) => {
    const element = document.getElementById(id);
    const style = element ? window.getComputedStyle(element) : null;
    const styleObject = style
      ? Object.fromEntries(
          Array.from(style).map((key: string) => [
            key,
            style.getPropertyValue(key),
          ])
        )
      : null;
    return styleObject
      ? (convertObjectKeysFromKebabCaseToCamelCase(
          styleObject
        ) as CSSStyleDeclaration)
      : null;
  };

  return {
    updateElementData,
    updateSelectedElementsData,
    getElementData,
    getSelectedElementsData,
    getSelectedElementsPropInputs,
    getElementComputedStyle,
  };
} 